import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { Action } from '../../types/toolbar';
import { Toolbar } from '../../components/ui/toolbar/toolbar.component';
import CustomModal from '../../components/ui/modal/modal.component';
import { EditPermissionGroup } from '../../workspaces/tenantConfig/editPermissionGroup/editPermissionGroup.component';
import { RemovePermissionGroup } from '../../workspaces/tenantConfig/removePermissionGroup/removePermissionGroup.component';
import { useCRUD } from '../../api/crud';
import { PERMISSION_GROUPS_URL } from '../../api/permissions';
import { PermissionGroup } from '../../types/permissions';
import { useHasPermission } from '../../contexts/auth';
import { TENANT_ROUTES } from '../../workspaces/tenantConfig/tenantConfig.routes';

export const TenantPermissionGroupActions = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get } = useCRUD(PERMISSION_GROUPS_URL);
  const [editGroupOpened, setEditGroupOpened] = useState<boolean>(false);
  const [removeGroupOpened, setRemoveGroupOpened] = useState<boolean>(false);
  const [group, setGroup] = useState<PermissionGroup | undefined>(undefined);
  const canChangeGroup = useHasPermission("auth.change_group");
  const canDeleteGroup = useHasPermission("auth.delete_group");

  const fetchGroup = useCallback(async () => {
    if (!id) return;

    const result = await get(id);

    if (result?.success && result?.data) {
      setGroup(result.data);
    }
  }, [id, get]);

  useEffect(() => {
    fetchGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t('Edit permission group'),
      onClick: () => {
        setEditGroupOpened(true);
      },
      disabled: !canChangeGroup,
    },
    {
      icon: <DeleteIcon />,
      label: t('Delete permission group'),
      onClick: () => {
        setRemoveGroupOpened(true);
      },
      disabled: !canDeleteGroup,
      color: "var(--errorColor)",
    },
  ];

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={editGroupOpened}
        setOpened={setEditGroupOpened}
        title={t('Edit permission group')}
        content={
          <EditPermissionGroup
            group={group}
            onSuccess={() => {
              setEditGroupOpened(false);
              fetchGroup();
            }}
          />
        }
      />
      <CustomModal
        opened={removeGroupOpened}
        setOpened={setRemoveGroupOpened}
        title={t('Delete permission group')}
        content={
          <RemovePermissionGroup
            group={group}
            onSuccess={() => {
              setRemoveGroupOpened(false);
              navigate(TENANT_ROUTES.permissionGroups);
            }}
          />
        }
      />
    </>
  );
};
